import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Import the SVG files
import HeroImage from "../assets/photoGroup1.webp";
import Logo from "../assets/logo.svg";
import LogoTransparent from "../assets/logoTransparent.svg";
import ProfessorsImage from "../assets/photoGroup2.webp";
import RecommendationsImage from "../assets/photoGroup3.webp";
import SearchImage from "../assets/photoGroup4.webp";
import { ReactComponent as DotGroup1 } from "../assets/dotGroup1.svg";
import { ReactComponent as DotGroup2 } from "../assets/dotGroup2.svg";
import HeroFindMatches from "./HeroFindMatches";
import FeedbackModal from "./FeedbackModal";
import { useGuestPreview } from "../context/GuestPreviewContext";

const NormalHome = () => {
    const navigate = useNavigate();
    const { isGuestPreview } = useGuestPreview();
    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

    const goToSignUp = () => {
        navigate('/register'); // Navigate to the signup page
    };

    const goToLoginOrApp = () => {
        if (isGuestPreview) {
            navigate('/login', { state: { fromGuestPreview: true } });
            return;
        }
        if (localStorage.getItem('authToken')) {
            navigate('/recommendations');
        } else {
            navigate('/login');
        }
    };

    // Scroll to section function
    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <div>
            <FeedbackModal
                open={isFeedbackOpen}
                onClose={() => setIsFeedbackOpen(false)}
                requireEmailCaptcha
            />
            {/* Navbar */}
            <nav className="bg-white border-b-[0.8px] border-[#202531] h-20 z-50 relative">
                <div className="max-w-full px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-full">
                        {/* Left Section: Logo */}
                        <Link to="/" className="flex items-center py-4" title="Home">
                            <img src={Logo} alt="Grad Connect" className="h-14" />
                        </Link>

                        {/* Right Section: Links and Buttons */}
                        <div className="flex items-center space-x-6 text-lg">
                            <button
                                onClick={() => scrollToSection('about')}
                                className="text-black hover:text-[#0196BF] transition font-medium">
                                About
                            </button>
                            <button
                                onClick={() => scrollToSection('features')}
                                className="text-black hover:text-[#0196BF] transition font-medium">
                                Features
                            </button>
                            <div className="flex items-center space-x-4">
                                <button
                                    onClick={goToSignUp}
                                    className="border border-[#0196BF] text-black px-5 py-3 rounded-full hover:bg-[#E0F7FF] transition w-28 font-medium"
                                >
                                    Sign Up
                                </button>
                                <button
                                    type="button"
                                    onClick={goToLoginOrApp}
                                    className="border border-[#0196BF] bg-[#0196BF] text-white px-5 py-3 rounded-full hover:bg-[#017BA3] transition w-28 font-medium"
                                >
                                    Log In
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section id="about" className="relative flex flex-col lg:flex-row justify-between items-center px-6 sm:px-12 lg:px-24 py-16 bg-gray-50">
                {/* Background Circle */}
                <div
                    className="absolute top-[-216px] left-[-256px] w-[960px] h-[960px] bg-[#202531] opacity-10 rounded-full z-0"
                    aria-hidden="true"
                ></div>

                <div
                    className="absolute top-[400px] left-[1120px] w-[640px] h-[640px] bg-[#202531] opacity-10 rounded-full z-0"
                    aria-hidden="true"
                ></div>

                {/* Text Section with Motion */}
                <div className="lg:w-1/2 text-left relative z-10" style={{ transform: 'translateY(-48px)' }}>
                    {/* First Text (p) */}
                    <p
                        className="text-4xl text-[#434D5C] max-w-[480px]"
                    >
                        Success in grad school begins with the right advisor.
                    </p>

                    {/* Second Text (h1) with Delay */}
                    <motion.h1
                        className="text-5xl sm:text-5xl font-bold text-gray-900 mt-4 max-w-[560px]"

                        initial={{ opacity: 0 }}

                        animate={{ opacity: 1 }}

                        transition={{ duration: 1.1, delay: 0.35, ease: "easeOut" }}

                    >

                        Grad Connect helps you find your perfect match.

                    </motion.h1>

                    <HeroFindMatches layout="normal" />
                </div>

                {/* Image Section */}
                <div className="lg:w-1/2 mt-8 lg:mt-0 flex justify-end">
                    <img src={HeroImage} className="w-full h-auto max-w-[560px] transform translate-x-[4%] translate-y-[-4%] transition-all duration-300" />
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="px-6 sm:px-12 lg:px-24 py-16 bg-gray-50">
                {/* Every Professor in One Place */}
                <div
                    className="lg:flex justify-between items-center"
                    style={{ transform: 'translate(40px, -40px)' }} // Scaled down transform
                >
                    <div
                        className="lg:w-1/2 text-left max-w-[480px]"
                    >
                        <h2 className="text-4xl font-bold text-gray-900 mb-6 whitespace-nowrap">
                            Every Professor in One Place
                        </h2>
                        <p className="text-xl text-gray-700 mb-8">
                            No more searching school directories. Grad Connect has all the information you need to evaluate advisors from every university in the US.
                        </p>
                        <div className="space-y-6">
                            <div
                                className="flex items-center max-w-[440px] pl-6"
                            >
                                <span className="text-[#FDA800] text-3xl mr-3">✔</span>
                                <p className="text-xl text-gray-700">Clear Research Summaries</p>
                            </div>
                            <div
                                className="flex items-center max-w-[320px] pl-6"
                            >
                                <span
                                    className="text-[#E50C33] text-3xl mr-3 self-start"
                                    style={{ transform: 'translateY(40%)' }}
                                >
                                    ✔
                                </span>
                                <p className="text-xl text-gray-700">
                                    Links to Personal Website and Google Scholar Profile
                                </p>
                            </div>
                            <div
                                className="flex items-center max-w-[440px] pl-6"
                            >
                                <span className="text-[#0196BF] text-3xl mr-3">✔</span>
                                <p className="text-xl text-gray-700">Contact Information</p>
                            </div>
                        </div>
                    </div>

                    {/* Image Section */}
                    <div className="lg:w-1/2 mt-8 lg:mt-0 flex justify-end">
                        <img src={ProfessorsImage} className="w-full h-auto max-w-[480px] transform translate-x-[-4%] translate-y-[4%]" />
                    </div>
                </div>
            </section>

            {/* Intelligent Recommendations */}
            <section className="relative px-6 sm:px-12 lg:px-24 py-16 bg-gray-50">
                {/* Background Circle */}
                <div
                    className="absolute top-[-160px] left-[-320px] w-[720px] h-[720px] bg-[#202531] opacity-10 rounded-full"
                    aria-hidden="true"
                ></div>
                <div
                    className="absolute top-[320px] right-[-320px] w-[640px] h-[640px] bg-[#202531] opacity-10 rounded-full"
                    aria-hidden="true"
                ></div>

                <div className="lg:flex justify-between items-center">
                    {/* Image Section */}
                    <div className="lg:w-1/2 mt-8 lg:mt-0 flex justify-start transform translate-x-[-4%] translate-y-[-12%]">
                        <img src={RecommendationsImage} className="w-full h-auto max-w-[400px]" />
                    </div>

                    {/* Text Section */}
                    <div
                        className="lg:w-1/2 text-left -mt-52 -ml-4"
                    >
                        <h2
                            className="text-4xl font-bold text-gray-900 mb-6"
                        >
                            Intelligent Recommendations
                        </h2>
                        <p
                            className="text-xl text-gray-700 max-w-[480px] mb-8"
                        >
                            Grad Connect helps you discover professors whose research aligns with your interests—even ones from schools you’ve never considered.
                        </p>
                        <p
                            className="text-xl max-w-[480px] text-gray-700"
                        >
                            The more you interact with professor profiles, the better the recommendations become.
                        </p>
                    </div>
                </div>
            </section>

            {/* Organize Your Search */}
            <section className="px-6 sm:px-12 lg:px-24 py-16 bg-gray-50 -mb-32">
                <div className="lg:flex justify-between items-center" style={{ transform: 'translate(40px, -40px)' }}>
                    {/* Text Section */}
                    <div
                        className="lg:w-1/2 text-left translate-y-[-200px] -mt-48"
                    >
                        <h2
                            className="text-4xl font-bold text-gray-900 mb-6 whitespace-nowrap"
                        >
                            Organize Your Search
                        </h2>
                        <p
                            className="text-xl text-gray-700 mb-8 max-w-[480px]"
                        >
                            Grad Connect lets you search for professors by name, school, or research topic. You can filter results by school or department, and easily bookmark professors you're interested in.
                        </p>
                        <p
                            className="text-xl text-gray-700 max-w-[480px]"
                        >
                            All their information in one place, making it simple to reach out when you're ready.
                        </p>
                    </div>

                    {/* Image Section */}
                    <div className="lg:w-1/2 mt-8 lg:mt-0 flex justify-end">
                        <img src={SearchImage} className="w-full h-auto max-w-[560px] transform translate-x-[4%] translate-y-[-24%]" />
                    </div>
                </div>
            </section>

{/* New "The Best Part" Section */}
            <div className="border-t-[0.8px] border-[#202531] w-screen"></div>

<section className="relative flex flex-col items-center py-24 bg-gray-50">
    {/* Decorative Dot Backgrounds */}
    <div className="absolute top-[10%] left-[10%] w-[120px] h-[120px] z-0 transform rotate-[95deg]">
        <DotGroup1 className="w-full h-full" />
    </div>
    <div className="absolute top-[60%] left-[80%] w-[120px] h-[120px] z-0 transform rotate-[85deg]">
        <DotGroup2 className="w-full h-full" />
    </div>

    {/* Text */}
    <h2
        className="text-3xl font-bold text-gray-900 mb-3 relative z-10"
    >
        The Best Part?
    </h2>

    <h3
        className="text-4xl font-extrabold text-[#E50C33] mb-6 relative z-10"
    >
        Grad Connect is free for students.
    </h3>

    {/* Button */}
    <button
        onClick={goToSignUp}
        className="bg-[#0196BF] text-white font-bold text-2xl px-6 py-3 rounded-full hover:bg-[#017BA3] transition relative z-10"
    >
        Get Started Now
    </button>
</section>




{/* Footer */}
<div className="border-t-[0.8px] border-[#202531] w-screen"></div> {/* Original border thickness */}
<footer className="bg-[#E0E0E2] py-4">
    <div className="mx-auto text-center px-8 max-w-[1200px]">
        {/* Logo */}
        <div className="mb-4">
            <img src={LogoTransparent} alt="GradConnect Logo" className="h-12 mx-auto" />
        </div>

        {/* Links */}
        <div className="flex justify-center space-x-2 mb-4">
            <a href="/register" className="text-gray-700 hover:text-[#0196BF] transition">
                Sign Up
            </a>
            <span className="text-gray-700">•</span> {/* Dot separator */}
            <a href="/login" className="text-gray-700 hover:text-[#0196BF] transition">
                Sign In
            </a>
            <span className="text-gray-700">•</span> {/* Dot separator */}
            <button
                type="button"
                onClick={() => setIsFeedbackOpen(true)}
                className="text-gray-700 hover:text-[#0196BF] transition"
            >
                Give feedback
            </button>
        </div>

        {/* Copyright */}
        <p className="text-sm text-gray-600 mt-4">
            &copy; 2026 GradConnect LLC
        </p>
    </div>
</footer>
        </div>
    );
};

export default NormalHome;
